import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { RootState } from 'store';

import SnackBar from 'components/SnackBar';
import { JOB_MODAL, projectModalActions, TASK_MODAL } from 'features/Project/projectModalSlice';
import { projectFilterActions } from 'features/Project/projectSlice';
import { ProjectListContainer } from 'pages/ProjectManagement/styles';
import { JOB_DRAWER_TAB } from 'pages/ProjectManagementOverview/ProjectManagementOverview';
import { useGetJobByProjectIdQuery, useGetTaskAssigneesQuery } from 'services/busmanApi';

import JobDrawer from './components/JobDrawer';
import TaskDrawer from './components/TaskDrawer';
import ProjectJobBoard from './sections/ProjectJobBoard';
import ProjectJobList from './sections/ProjectJobList';

interface ProjectManagementJobProps {
  sideJobTabIndex: number;
}

const ProjectManagementJob = ({ sideJobTabIndex }: ProjectManagementJobProps) => {
  const dispatch = useDispatch();
  const { projectID } = useParams();
  const [message, setMessage] = React.useState({ type: '', msg: '' });
  const { open, mode, modalProps } = useSelector((state: RootState) => state.projectModalReducer);
  const { selectedProjectScope } = useSelector((state: RootState) => state.projectFilterReducer);

  const { data: projectJobs, isLoading } = useGetJobByProjectIdQuery({
    projectId: projectID,
    projectScope: selectedProjectScope,
  });

  const jobIds = projectJobs?.map((item) => item.job_id);
  const { data: assigneeList } = useGetTaskAssigneesQuery(jobIds && jobIds.length > 0 ? jobIds : skipToken);

  // Set Assignee list to Redux
  React.useEffect(() => {
    if (assigneeList) {
      dispatch(projectFilterActions.setJobAssigneeList(assigneeList));
    }
  }, [assigneeList, dispatch]);

  // Set Project Jobs list to Redux
  React.useEffect(() => {
    if (projectJobs) {
      const minProjectJobs = projectJobs.map((job) => ({
        job_id: job.job_id,
        job_name: job.job_name,
        client_name: job.client.client_name,
        job_description: job.job_description,
        introduced_in_scope_id: job.introduced_in_scope_id,
      }));
      dispatch(projectFilterActions.setProjectJobs(minProjectJobs));
    }
  }, [dispatch, projectJobs]);

  return (
    <>
      <ProjectListContainer sx={{ padding: 4 }}>
        {sideJobTabIndex === JOB_DRAWER_TAB.LIST && (
          <ProjectJobList projectJobs={projectJobs || []} isLoading={isLoading} />
        )}
        {sideJobTabIndex === JOB_DRAWER_TAB.BOARD && (
          <ProjectJobBoard projectJobs={projectJobs || []} isLoading={isLoading} />
        )}

        {open && modalProps?.type === JOB_MODAL && (
          <JobDrawer
            open={open}
            isEditing={mode === 'edit'}
            setSnackBarMessage={setMessage}
            onClose={() => dispatch(projectModalActions.closeModal())}
            initialValues={modalProps?.type === JOB_MODAL ? modalProps.data : null}
          />
        )}
        {open && modalProps?.type === TASK_MODAL && (
          <TaskDrawer
            open={open}
            isEditing={mode === 'edit'}
            setSnackBarMessage={setMessage}
            onClose={() => dispatch(projectModalActions.closeModal())}
            initialValues={modalProps?.type === TASK_MODAL ? modalProps.data : null}
          />
        )}
      </ProjectListContainer>
      <SnackBar
        message={message}
        onClose={() => setMessage({ type: '', msg: '' })}
        snackbarOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ProjectManagementJob;
